import {
  useHttpClient,
  useNetworkStatus,
  useReducerStorage as useReducer,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { OrganizationSOW } from './organizationSOWs.models';
import { initState, SOWReducer, SOWState } from './organizationSOWs.reducer';

interface OrganizationSOWsContextProps {
  sows: OrganizationSOW[];
  currentSOW: OrganizationSOW | null;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  fetchSOWs: (organizationId: string) => Promise<void>;
}

const OrganizationSOWsContext = createContext<
  OrganizationSOWsContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<SOWState>;
}>;

export const OrganizationSOWsProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(
    SOWReducer,
    { ...initState, ...initialState },
    {
      key: 'airship-cache:organization-sows',
      select: (s) => ({ ids: s.ids, entities: s.entities }),
    }
  );
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const sows = useMemo(
    () => state.ids.map((id) => state.entities[id]),
    [state.entities, state.ids]
  );

  const currentSOW = useMemo(
    () => state.entities[state.currentSOWId || ''],
    [state.currentSOWId, state.entities]
  );

  const fetchSOWs: OrganizationSOWsContextProps['fetchSOWs'] = useCallback(
    async (organizationId) => {
      if (!isOnline()) {
        dispatch({
          type: 'SET_LOADING_STATE',
          payload: { isLoading: false, isLoaded: true },
        });
        return;
      }

      dispatch({ type: 'FETCH_SOWS' });
      try {
        const sows = await httpClient.get<OrganizationSOW[]>(
          `/organizations/${organizationId}/sow`
        );
        dispatch({
          type: 'FETCH_SOWS_SUCCESS',
          payload: { sows },
        });
      } catch (error) {
        dispatch({
          type: 'FETCH_SOWS_FAILURE',
          payload: { error: `Failed to fetch SOWs: ${error}` },
        });
        throw error;
      }
    },
    [httpClient, isOnline]
  );

  return (
    <OrganizationSOWsContext.Provider
      value={{
        sows: sows,
        currentSOW: currentSOW,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        error: state.error,
        fetchSOWs,
      }}
    >
      {children}
    </OrganizationSOWsContext.Provider>
  );
};

export const useOrganizationSOWs = () => {
  const context = useContext(OrganizationSOWsContext);
  if (context === undefined) {
    throw new Error(
      'useOrganizationSOWs must be used within a OrganizationSOWsProvider'
    );
  }

  return context;
};
